import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { signOut } from "../../store/slices/authSlice";
import { apiClient } from "../../api/client";
import styled from "styled-components";
import { redirectToLogin } from "../../utils/oauth";
import {
  clearLoginAttempts,
  isLoginLoopDetected,
  recordLoginAttempt,
} from "../../utils/authLoopGuard";
import { AUTH_URL, SERVER_URL } from "../../config/env";
import { toSameOriginPath } from "../../utils/sameOriginPath";

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #fdbe7c;
  padding: 20px;
  text-align: center;
`;

const LoadingText = styled.p`
  font-size: 1.2rem;
  color: #223126;
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  margin: 8px;
  background-color: #f4ab47;
  border: 1px solid black;
  cursor: pointer;
  font: inherit;
`;

/**
 * login page - redirects to auth service via OAuth flow
 *
 * uses OAuth 2.0 authorization code flow with PKCE for security.
 * scopes requested: openid profile email backend:read backend:write
 *
 * if already logged in, redirects to home (or the ?return= path)
 * otherwise initiates OAuth flow to auth service
 *
 * The redirect is automatic and the IdP answers it silently whenever an SSO
 * session is live, which means a persistent downstream failure would otherwise
 * loop here forever without ever showing a password prompt. authLoopGuard caps
 * that; after a few bounces in a minute we stop and offer a real way out.
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    document.title = "NΛSOS Log In";
  }, []);

  useEffect(() => {
    // if already logged in, honour the return URL the session manager captured.
    // toSameOriginPath accepts absolute same-origin URLs too (three components
    // pass window.location.href) and does not double-decode, which used to throw
    // URIError on any path containing a bare '%'.
    if (token) {
      clearLoginAttempts();
      navigate(toSameOriginPath(searchParams.get("return")) ?? "/", {
        replace: true,
      });
      return;
    }

    if (isLoginLoopDetected() || !recordLoginAttempt()) {
      console.warn("[Login] repeated sign-in attempts detected, not redirecting again");
      setStuck(true);
      return;
    }

    // initiate OAuth flow with PKCE
    // this redirects to auth service /oauth/consent which handles login if needed
    redirectToLogin();
  }, [token, navigate, searchParams]);

  if (stuck) {
    // Both buttons have to end the server-side session, not just the local
    // state. The BFF session cookie survives a bare navigation, so "continue
    // signed out" would leave the user signed in again after one refresh, and
    // "sign out and try again" would end the IdP session while leaving a live
    // BFF refresh token behind on a shared machine.
    const endLocalSession = async () => {
      clearLoginAttempts();
      try {
        await apiClient.post(
          `${SERVER_URL}/api/auth/logout`,
          {},
          { withCredentials: true }
        );
      } catch (error) {
        console.warn("[Login] Failed to end the server session:", error);
      }
      dispatch(signOut());
    };

    const postLogout = encodeURIComponent(`${window.location.origin}/login`);
    return (
      <LoadingContainer>
        <h2>Sign-in keeps restarting</h2>
        <LoadingText>
          We signed you in but the session was rejected again straight away, so we
          stopped retrying. Signing out of the identity service and starting over
          usually clears it.
        </LoadingText>
        <div>
          <ActionButton
            onClick={async () => {
              await endLocalSession();
              // ending the IdP session is what forces a real password prompt
              // instead of another silent auto-approval
              window.location.href = `${AUTH_URL}/oauth/logout?client_id=n450s_web&post_logout_redirect_uri=${postLogout}`;
            }}
          >
            sign out and try again
          </ActionButton>
          <ActionButton
            onClick={async () => {
              await endLocalSession();
              navigate("/");
            }}
          >
            continue signed out
          </ActionButton>
        </div>
      </LoadingContainer>
    );
  }

  // show loading while redirecting
  return (
    <LoadingContainer>
      <LoadingText>Redirecting to login...</LoadingText>
    </LoadingContainer>
  );
};

export default LoginPage;
